import React from 'react';
import { FaTimes, FaVideo, FaFilePdf, FaVolumeUp } from 'react-icons/fa';

interface Solution {
  videoUrl?: string;
  audioUrl?: string;
  pdfUrl?: string;
  title?: string;
}

interface SolutionSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  solutions: Solution[] | string[];
  onSelectSolution: (solution: Solution | string) => void;
  questionTitle?: string;
  isMobile?: boolean;
}

const SolutionSelector: React.FC<SolutionSelectorProps> = ({ 
  isOpen, 
  onClose, 
  solutions, 
  onSelectSolution,
  questionTitle,
  isMobile = false 
}) => {
  if (!isOpen || !solutions || solutions.length === 0) return null;

  // If only one solution, directly show it
  if (solutions.length === 1) {
    const solution = solutions[0];
    if (typeof solution === 'string') {
      onSelectSolution(solution);
      return null;
    } else if (solution.videoUrl) {
      onSelectSolution(solution.videoUrl);
      return null;
    }
  }

  return (
    <div className={`fixed inset-0 z-[60] flex ${isMobile ? 'items-end' : 'items-center justify-center'}`}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={`relative w-full ${isMobile ? 'rounded-t-2xl max-h-[80vh]' : 'max-w-2xl mx-4 rounded-lg'} bg-white dark:bg-gray-800 shadow-2xl overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 flex justify-between items-center">
          <h3 className="text-white font-semibold text-lg">
            {questionTitle ? `${questionTitle} - Çözümler` : 'Çözüm Seçenekleri'}
          </h3>
          <button
            onClick={onClose}
            className={`${isMobile ? 'p-2.5' : 'p-2'} rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors`}
            title="Kapat"
            aria-label="Kapat"
          >
            <FaTimes size={isMobile ? 20 : 16} />
          </button>
        </div>
        
        {/* Solutions List */}
        <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-3 ${isMobile ? 'flex-1' : 'max-h-[60vh]'} overflow-y-auto -webkit-overflow-scrolling-touch`}>
          {solutions.map((solution, index) => {
            // Handle string array (legacy format)
            if (typeof solution === 'string') {
              return (
                <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <button
                    onClick={() => onSelectSolution(solution)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors w-full justify-center"
                  >
                    <FaVideo />
                    <span>Video Çözüm {index + 1}</span>
                  </button>
                </div>
              );
            }
            
            // Handle object format
            return (
            <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                {solution.title || `Çözüm ${index + 1}`}
              </h4>
              
              <div className="flex flex-wrap gap-2">
                {solution.videoUrl && (
                  <button
                    onClick={() => onSelectSolution(solution.videoUrl!)}
                    className={`flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors ${isMobile ? 'w-full justify-center' : ''}`}
                  >
                    <FaVideo />
                    <span>Video Çözüm</span>
                  </button>
                )}
                
                {solution.pdfUrl && (
                  <a
                    href={solution.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                  >
                    <FaFilePdf />
                    <span>PDF Çözüm</span>
                  </a>
                )}
                
                {solution.audioUrl && (
                  <a
                    href={solution.audioUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                  >
                    <FaVolumeUp />
                    <span>Sesli Çözüm</span>
                  </a>
                )}
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SolutionSelector;